import React, { useState, useEffect, useRef } from 'react';

export default function GesturePanel({ isCameraActive }) {
  const [gestureState, setGestureState] = useState({
    leftDetected: false,
    rightDetected: false,
    leftOpenness: 0,
    rightOpenness: 0,
    avgOpenness: 0,
    scale: 1.0
  });

  // Listen for gesture updates from the hand tracking hook
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.gestureState) {
        setGestureState(window.gestureState);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);
  const [position, setPosition] = useState({ x: null, y: null });
  const panelRef = useRef(null);
  const isDraggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const savedPosition = localStorage.getItem('gesturePanelPosition');
    if (savedPosition) {
      const pos = JSON.parse(savedPosition);
      setPosition(pos);
      if (panelRef.current) {
        panelRef.current.style.left = pos.x + 'px';
        panelRef.current.style.top = pos.y + 'px';
        panelRef.current.style.right = 'auto';
        panelRef.current.style.bottom = 'auto';
      }
    }
  }, []);

  const getActionText = () => {
    const { leftDetected, rightDetected, avgOpenness } = gestureState;
    if (!leftDetected && !rightDetected) {
      return '👋 Show your hands to the camera';
    }

    let text = '';
    if (avgOpenness < 0.2) {
      text = '✊ Fist detected - Particles shrinking';
    } else if (avgOpenness < 0.4) {
      text = '🤏 Partially closed - Particles compact';
    } else if (avgOpenness < 0.6) {
      text = '✋ Half open - Particles medium size';
    } else if (avgOpenness < 0.8) {
      text = '🖐️ Mostly open - Particles expanding';
    } else {
      text = '👋 Fully open - Particles maximized';
    }

    if (leftDetected && rightDetected) {
      text += ' (Both hands)';
    } else if (leftDetected) {
      text += ' (Left hand)';
    } else {
      text += ' (Right hand)';
    }

    return text;
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.gesture-panel-header')) {
      isDraggingRef.current = true;
      const rect = panelRef.current.getBoundingClientRect();
      offsetRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
      panelRef.current.classList.add('dragging');
    }
  };

  const handleMouseMove = (e) => {
    if (isDraggingRef.current && panelRef.current) {
      const x = e.clientX - offsetRef.current.x;
      const y = e.clientY - offsetRef.current.y;
      const maxX = window.innerWidth - panelRef.current.offsetWidth;
      const maxY = window.innerHeight - panelRef.current.offsetHeight;
      
      const constrainedX = Math.max(0, Math.min(x, maxX));
      const constrainedY = Math.max(0, Math.min(y, maxY));

      panelRef.current.style.left = constrainedX + 'px';
      panelRef.current.style.top = constrainedY + 'px';
      panelRef.current.style.right = 'auto';
      panelRef.current.style.bottom = 'auto';
    }
  };

  const handleMouseUp = () => {
    if (isDraggingRef.current && panelRef.current) {
      isDraggingRef.current = false;
      panelRef.current.classList.remove('dragging');
      const rect = panelRef.current.getBoundingClientRect();
      const pos = { x: rect.left, y: rect.top };
      setPosition(pos);
      localStorage.setItem('gesturePanelPosition', JSON.stringify(pos));
    }
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div className="gesture-panel" ref={panelRef} onMouseDown={handleMouseDown}>
      <div className="gesture-panel-header">
        <h3>Gesture Detection</h3>
        <div className="header-controls">
          <button 
            className={`detection-toggle ${isCameraActive ? 'active' : ''}`}
            id="detection-toggle" 
            title={isCameraActive ? "Stop camera" : "Start camera"}
            onClick={() => {
              if (isCameraActive) {
                window.stopCamera?.();
              } else {
                window.startCamera?.();
              }
            }}
          >
            <span className="toggle-icon">{isCameraActive ? '■' : '▶'}</span>
          </button>
          <span className="drag-handle" title="Drag to move">⋮⋮</span>
        </div>
      </div>
      <div className="gesture-status">
        <div className="hand-status">
          <div className="hand-indicator">
            <span className="hand-label">Left Hand:</span>
            <span className={`hand-state ${gestureState.leftDetected ? 'detected' : ''}`}>
              {gestureState.leftDetected ? 'Detected' : 'Not detected'}
            </span>
            {gestureState.leftDetected ? (
              <>
                <div className="openness-bar">
                  <div 
                    className="openness-fill" 
                    style={{ width: `${gestureState.leftOpenness * 100}%` }}
                  />
                </div>
                <span className="openness-value">
                  {Math.round(gestureState.leftOpenness * 100)}%
                </span>
              </>
            ) : (
              <>
                <div className="openness-bar">
                  <div className="openness-fill" style={{ width: '0%' }} />
                </div>
                <span className="openness-value">0%</span>
              </>
            )}
          </div>
          <div className="hand-indicator">
            <span className="hand-label">Right Hand:</span>
            <span className={`hand-state ${gestureState.rightDetected ? 'detected' : ''}`}>
              {gestureState.rightDetected ? 'Detected' : 'Not detected'}
            </span>
            {gestureState.rightDetected ? (
              <>
                <div className="openness-bar">
                  <div 
                    className="openness-fill" 
                    style={{ width: `${gestureState.rightOpenness * 100}%` }}
                  />
                </div>
                <span className="openness-value">
                  {Math.round(gestureState.rightOpenness * 100)}%
                </span>
              </>
            ) : (
              <>
                <div className="openness-bar">
                  <div className="openness-fill" style={{ width: '0%' }} />
                </div>
                <span className="openness-value">0%</span>
              </>
            )}
          </div>
        </div>
        <div className="gesture-action">
          <div className="action-label">Current Action:</div>
          <div className="action-text">{getActionText()}</div>
          <div className="scale-indicator">
            <span>Particle Scale: </span>
            <span>{gestureState.scale.toFixed(2)}x</span>
          </div>
        </div>
      </div>
    </div>
  );
}

